import { useEffect, useState, useMemo } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useParams, Link, useNavigate } from "react-router-dom"
import {
  Users, BarChart2, List, Download, Filter,
  ArrowLeft, CheckCircle2, XCircle, AlertCircle, ChevronDown,
  TrendingUp, Calendar
} from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { SkeletonList } from "@/clientWeb/components/ui/page-loader"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/clientWeb/components/ui/dropdown-menu"
import {
  meetingHistoryService,
  type AttendanceStatus,
  type AttendanceTimelineRecord,
} from "./services/meeting-history.service"
import { useMeetingsStore, selectMeetingById } from "./store/meetings.store"

type ViewMode = "graph" | "table"
type FilterType = "all" | "present" | "late" | "absent"

type AttendeeRecord = AttendanceTimelineRecord

/* ─── Avatar ───────────────────────────────────────────────────────── */
function Avatar({ name, imageUrl, size = 32 }: { name: string; imageUrl?: string; size?: number }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
  const colors = ["bg-primary/20 text-primary", "bg-violet-500/20 text-violet-600", "bg-emerald-500/20 text-emerald-600", "bg-amber-500/20 text-amber-600"]
  const ci = name.charCodeAt(0) % colors.length
  return (
    <div className={`rounded-full flex items-center justify-center text-[10px] font-semibold shrink-0 overflow-hidden ${colors[ci]}`} style={{ width: size, height: size }}>
      {imageUrl ? <img src={imageUrl} alt={name} className="w-full h-full object-cover" /> : initials}
    </div>
  )
}

/* ─── Status badge ─────────────────────────────────────────────────── */
function StatusBadge({ status }: { status: AttendanceStatus }) {
  const t = useTranslation()
  const cfg = {
    present: { label: t("meetings.present"), cls: "bg-green-500/10 text-green-600 dark:text-green-400", Icon: CheckCircle2 },
    late:    { label: t("meetings.late"),    cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400",  Icon: AlertCircle },
    absent:  { label: t("meetings.absent"),  cls: "bg-red-500/10 text-red-600 dark:text-red-400",        Icon: XCircle     },
  }[status]
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cfg.cls}`}>
      <cfg.Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  )
}

/* ─── Graph Bar ────────────────────────────────────────────────────── */
function AttendanceBar({ attendee, totalMin }: { attendee: AttendeeRecord; totalMin: number }) {
  const t = useTranslation()
  const absent = attendee.status === "absent"
  const joinPct  = absent ? 0 : (attendee.joinTimestamp / totalMin) * 100
  const widthPct = absent ? 0 : ((attendee.leaveTimestamp - attendee.joinTimestamp) / totalMin) * 100
  const barColor = attendee.status === "present" ? "bg-green-500" : attendee.status === "late" ? "bg-amber-400" : "bg-red-400/30"

  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-border/40 last:border-0 group">
      <div className="flex items-center gap-2 w-36 shrink-0 min-w-0">
        <Avatar name={attendee.name} imageUrl={attendee.imageUrl} size={26} />
        <span className="text-xs text-foreground truncate font-medium">{attendee.name.split(" ")[0]}</span>
      </div>
      <div className="flex-1 relative h-5 bg-muted/60 rounded-full overflow-hidden">
        {!absent && (
          <div
            className={`absolute top-0 h-full rounded-full transition-all ${barColor}`}
            style={{ left: `${joinPct}%`, width: `${widthPct}%` }}
          />
        )}
        {absent && (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-[10px] text-muted-foreground/50">{t("meetings.notAttended")}</span>
          </div>
        )}
      </div>
      <div className="w-16 text-right shrink-0">
        <span className="text-xs text-muted-foreground">{absent ? "—" : `${attendee.durationMin}m`}</span>
      </div>
    </div>
  )
}

/* ─── Main component ───────────────────────────────────────────────── */
export default function AttendancePage() {
  const t = useTranslation()
  const navigate = useNavigate()
  // The route is /meetings/:id/attendance. This read `?meetingId=`, which
  // nothing ever set, so every meeting asked for the same fixed id.
  const { id: meetingId = "" } = useParams()
  const meeting = useMeetingsStore(selectMeetingById(meetingId))

  const [view, setView] = useState<ViewMode>("graph")
  const [filter, setFilter] = useState<FilterType>("all")
  const [sortBy, setSortBy] = useState<"name" | "duration" | "join">("join")
  const [attendees, setAttendees] = useState<AttendeeRecord[]>([])
  const [meetingDur, setMeetingDur] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    meetingHistoryService.getAttendanceTimeline(meetingId)
      .then((data) => {
        if (cancelled) return
        setAttendees(data.attendees)
        setMeetingDur(data.meetingDurationMin)
      })
      .catch((err) => {
        if (cancelled) return
        toast.error({ title: 'Failed to load attendance', description: err instanceof Error ? err.message : 'Please try again' })
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [meetingId])

  const presentCount = attendees.filter(a => a.status === "present").length
  const lateCount    = attendees.filter(a => a.status === "late").length
  const absentCount  = attendees.filter(a => a.status === "absent").length
  const avgDuration  = Math.round(attendees.filter(a => a.durationMin > 0).reduce((s, a) => s + a.durationMin, 0) / (presentCount + lateCount) || 0)

  const noData = attendees.length === 0

  const filtered = useMemo(() => {
    let list = attendees
    if (filter !== "all") list = list.filter(a => a.status === filter)
    if (sortBy === "name")     list = [...list].sort((a, b) => a.name.localeCompare(b.name))
    if (sortBy === "duration") list = [...list].sort((a, b) => b.durationMin - a.durationMin)
    if (sortBy === "join")     list = [...list].sort((a, b) => a.joinTimestamp - b.joinTimestamp)
    return list
  }, [filter, sortBy, attendees])

  if (loading) {
    return (
      <div className="flex flex-col flex-1 overflow-hidden p-4">
        <SkeletonList rows={6} />
      </div>
    )
  }

  const handleExcelExport = () => {
    const rows = attendees.map(a => ({
      Name: a.name, Email: a.email, Status: a.status, "Join Time": a.joinTime,
      "Leave Time": a.leaveTime, "Duration (min)": a.durationMin,
    }))
    const csv = [Object.keys(rows[0]).join(","), ...rows.map(r => Object.values(r).join(","))].join("\n")
    const blob = new Blob([csv], { type: "text/csv" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url; a.download = "attendance.csv"; a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      {/* Header */}
      <div className="px-4 sm:px-6 py-3 border-b border-border bg-card shrink-0">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1 flex-wrap">
          <Link to="/meetings" className="hover:text-foreground transition-colors">Meetings</Link>
          <span>/</span>
          <Link to={`/meetings/${meetingId}/past`} className="hover:text-foreground transition-colors">{t("meetings.pastMeeting")}</Link>
          <span>/</span>
          <span className="text-foreground">{t("meetings.attendance")}</span>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-lg font-bold text-foreground">{t("meetings.attendanceReport")}</h1>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              {meeting && (
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar className="w-3.5 h-3.5" />{meeting.title}
                </span>
              )}
            </div>
          </div>
          <Button
            variant="ghost" size="sm"
            onClick={() => navigate(-1)}
            className="gap-1.5 text-xs shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5" />Back
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            // "—", not 0, while nothing is recorded: 0 would claim nobody came.
            { label: t("meetings.totalInvited"),  value: noData ? "—" : attendees.length, Icon: Users,        color: "text-foreground" },
            { label: t("meetings.present"),        value: noData ? "—" : presentCount + lateCount, Icon: CheckCircle2, color: "text-green-500" },
            { label: t("meetings.absent"),         value: noData ? "—" : absentCount, Icon: XCircle,       color: "text-red-500"   },
            { label: t("meetings.avgDuration"),   value: noData ? "—" : `${avgDuration}m`, Icon: TrendingUp,   color: "text-primary"   },
          ].map(({ label, value, Icon, color }) => (
            <div key={label} className="bg-card rounded-xl border border-border p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${color}`} />
                <span className="text-xs text-muted-foreground font-medium">{label}</span>
              </div>
              <span className={`text-2xl font-bold ${color}`}>{value}</span>
            </div>
          ))}
        </div>

        {noData ? (
          <div className="bg-card rounded-xl border border-border flex flex-col items-center justify-center py-16 text-center gap-2">
            <Users className="w-10 h-10 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">{t("meetings.attendanceNotRecorded")}</p>
          </div>
        ) : (<>
        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* View toggle */}
          <div className="flex items-center bg-muted rounded-lg p-1 gap-1">
            <button
              onClick={() => setView("graph")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${view === "graph" ? "bg-card shadow-sm text-primary" : "text-muted-foreground"}`}
            >
              <BarChart2 className="w-3.5 h-3.5" />{t("meetings.graph")}
            </button>
            <button
              onClick={() => setView("table")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${view === "table" ? "bg-card shadow-sm text-primary" : "text-muted-foreground"}`}
            >
              <List className="w-3.5 h-3.5" />{t("meetings.tableView")}
            </button>
          </div>

          {/* Filter */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 text-xs">
                <Filter className="w-3.5 h-3.5" />
                {filter === "all" ? t("meetings.all") : filter === "present" ? t("meetings.present") : filter === "late" ? t("meetings.late") : t("meetings.absent")}
                <ChevronDown className="w-3 h-3 ml-0.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {(["all", "present", "late", "absent"] as FilterType[]).map(f => (
                <DropdownMenuItem key={f} onClick={() => setFilter(f)} className={filter === f ? "font-semibold text-primary" : ""}>
                  {f === "all" ? t("meetings.all") : f === "present" ? t("meetings.present") : f === "late" ? t("meetings.late") : t("meetings.absent")}
                  {f !== "all" && (
                    <span className="ml-auto text-xs text-muted-foreground">
                      {f === "present" ? presentCount : f === "late" ? lateCount : absentCount}
                    </span>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Sort (table only) */}
          {view === "table" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5 text-xs">
                  {t("meetings.sortBy")} {sortBy === "name" ? "Name" : sortBy === "duration" ? "Duration" : t("meetings.sortJoinTime")}
                  <ChevronDown className="w-3 h-3 ml-0.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => setSortBy("join")}>{t("meetings.sortJoinTime")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy("name")}>Name</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy("duration")}>Duration</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={handleExcelExport}>
              <Download className="w-3.5 h-3.5" />{t("meetings.exportCSV")}
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          {view === "graph" ? (
            /* ── Graph view ── */
            <div className="p-4">
              {/* Time axis header */}
              <div className="flex items-center gap-3 mb-3 pb-2 border-b border-border/40">
                <div className="w-36 shrink-0" />
                <div className="flex-1 flex justify-between text-[10px] text-muted-foreground/60 font-mono px-1">
                  <span>10:00</span>
                  <span>10:15</span>
                  <span>10:30</span>
                  <span>10:45</span>
                  <span>11:00</span>
                  <span>11:15</span>
                </div>
                <div className="w-16 text-right text-[10px] text-muted-foreground/60 shrink-0">Time</div>
              </div>

              {/* Legend */}
              <div className="flex items-center gap-4 mb-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-green-500 shrink-0" />{t("meetings.present")}</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-400 shrink-0" />{t("meetings.late")}</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-400/30 shrink-0" />{t("meetings.absent")}</span>
              </div>

              {/* Bars */}
              <div>
                {filtered.map(a => (
                  <AttendanceBar key={a.id} attendee={a} totalMin={meetingDur} />
                ))}
              </div>
            </div>
          ) : (
            /* ── Table view ── */
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">{t("common.participant")}</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-muted-foreground">Status</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-muted-foreground">{t("meetings.joinTime")}</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-muted-foreground">{t("meetings.leaveTime")}</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {filtered.map(a => (
                    <tr key={a.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <Avatar name={a.name} imageUrl={a.imageUrl} size={30} />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{a.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{a.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3"><StatusBadge status={a.status} /></td>
                      <td className="px-3 py-3 text-xs text-foreground font-mono">{a.joinTime}</td>
                      <td className="px-3 py-3 text-xs text-foreground font-mono">{a.leaveTime}</td>
                      <td className="px-4 py-3 text-right">
                        {a.durationMin > 0 ? (
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-xs font-semibold text-foreground">{a.durationMin} min</span>
                            <div className="w-20 h-1 bg-muted rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${a.status === "present" ? "bg-green-500" : "bg-amber-400"}`}
                                style={{ width: `${Math.round((a.durationMin / meetingDur) * 100)}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground/50">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filtered.length === 0 && (
                <div className="py-12 text-center text-sm text-muted-foreground">{t("meetings.noRecordsFound")}</div>
              )}
            </div>
          )}
        </div>

        {/* Summary footer */}
        <div className="bg-muted/30 rounded-xl border border-border/50 px-4 py-3 flex items-center gap-4 flex-wrap text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
            <strong className="text-foreground">{presentCount}</strong> {t("meetings.onTime")}
          </span>
          <span className="flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
            <strong className="text-foreground">{lateCount}</strong> {t("meetings.late").toLowerCase()}
          </span>
          <span className="flex items-center gap-1.5">
            <XCircle className="w-3.5 h-3.5 text-red-500" />
            <strong className="text-foreground">{absentCount}</strong> {t("meetings.absent").toLowerCase()}
          </span>
          <span className="ml-auto">
            {t("meetings.attendanceRate")} <strong className="text-foreground">{Math.round(((presentCount + lateCount) / attendees.length) * 100)}%</strong>
          </span>
        </div>
        </>)}
      </div>
    </div>
  )
}
